import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ParentStorableData} from 'constants/SampleConstants';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import * as StorableActions from 'samples/StorableActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ParentLink from 'ui-library/ParentLink';

import ParentStorableBreadcrumbs from './ParentStorableBreadcrumbs';

const NULL_PARENT_STORABLE_DATA: ParentStorableData = {
  id: null,
  gridCellColumnIndex: null,
  gridCellRowIndex: null,
};

export interface ParentStorableDetailsInfo {
  id: string;
  name: string;
  parentStorableId?: string | null;
  storableSchema: {registryId: string};
}

export interface ParentStorableDetailsProps {
  isWritable: boolean;
  onSetParentStorable: (parentStorableData: ParentStorableData) => void;
  parentStorable?: ParentStorableDetailsInfo | null;
  // Wait for confirmation from before setting parent storable
  confirmBeforeEditing?: () => Promise<boolean>;
}

export default function ParentStorableDetails({
  isWritable,
  onSetParentStorable,
  parentStorable,
  confirmBeforeEditing,
}: ParentStorableDetailsProps): JSX.Element {
  const canUnsetParentStorable =
    !BenchlingConfig.ENABLE_REQUIRED_PARENT_STORABLE && isWritable && parentStorable;
  const breadcrumbs = <ParentStorableBreadcrumbs parentStorable={parentStorable} />;

  function handleClearParent(): void {
    onSetParentStorable(NULL_PARENT_STORABLE_DATA);
  }

  async function handleClearParentWithConfirmation(): Promise<void> {
    assert(confirmBeforeEditing != null, 'Expected confirmBeforeEditing to be provided');
    const shouldProceed = await confirmBeforeEditing();
    if (shouldProceed) {
      handleClearParent();
    }
  }

  return (
    <div className="u-flex-display" {...getTestProps({component: 'ParentStorableDetails', element: 'root'})}>
      {parentStorable ? (
        <ParentLink
          icon={ModelTypeConstants.LOCATION}
          onClickParent={() => {
            StorableActions.open(parentStorable);
          }}
          parentName={breadcrumbs}
          tooltipTitle="Show containing location"
        />
      ) : (
        <div>None</div>
      )}
      {canUnsetParentStorable && (
        <div className="u-mini-spacing-left u-primary-pointer">
          <Tooltip placement={TooltipPlacement.TOP} title="Unset inventory...">
            <BenchlingIcon
              altText="Unset inventory"
              className="containerLocationUnset-icon faded"
              dataTestAttrs={{component: 'ParentStorableDetails', element: 'unsetParentButton'}}
              icon="close"
              {...getActionButtonRoleProps({
                onPress: confirmBeforeEditing != null ? handleClearParentWithConfirmation : handleClearParent,
              })}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
}
